package com.swetdot.swetgram.domain

import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * Pure timeline logic: grouping into days and deciding which bubbles carry an avatar and a
 * tail. Kept free of Compose on purpose -- this is the part of the chat screen that is easy to
 * get subtly wrong and, unlike the layout, can be tested on a machine with no device attached.
 */

/** One rendered row. The list is built newest-first, matching the reversed lazy list. */
sealed interface TimelineItem {
    data class MessageRow(
        val message: Message,
        /** Show the sender's avatar and name -- true only on the last bubble of a run. */
        val showSender: Boolean,
        /** Tail on the bubble; also the last of a run, visually the "end" of the group. */
        val isRunEnd: Boolean,
        val isRunStart: Boolean,
    ) : TimelineItem

    /** Sticky separator between calendar days. */
    data class DaySeparator(val day: LocalDay) : TimelineItem
}

data class LocalDay(val year: Int, val month: Int, val day: Int) {

    /**
     * The same day as a `kotlinx-datetime` [LocalDate], which is where calendar arithmetic lives.
     *
     * THIS TYPE IS THREE INTEGERS AND KNOWS NOTHING. It is deliberately dumb -- it exists so a
     * timeline can group and compare days without dragging a date library through every equality
     * check -- but "which weekday is this" and "how many days apart are these" are real calendar
     * questions, and the answers are wrong in ways nobody can see. A Zeller congruence or a
     * `(epochDays + 4) % 7` shifts every day of the week at once and reads as perfectly plausible;
     * this host has no device to screenshot it on, so the first person to notice would be a user.
     *
     * `LocalDate` already answers both, correctly, on Android and iOS alike, and the library is
     * already a dependency. So the conversion is one line and the arithmetic is nobody's here.
     *
     * `LocalDate(year, month, day)` THROWS on an impossible date (month 13, February 30). Every
     * `LocalDay` the app builds comes from [of], which reads a real instant, so the only way to
     * reach that is to hand-write one in a test -- where throwing is the correct answer.
     */
    fun toLocalDate(): LocalDate = LocalDate(year, month, day)

    /** MONDAY..SUNDAY, from the calendar rather than from a modulo trick. See [toLocalDate]. */
    val dayOfWeek: DayOfWeek get() = toLocalDate().dayOfWeek

    /**
     * Days since 1970-01-01, which is how two calendar days are subtracted.
     *
     * EXACT ACROSS DST, which subtracting 86400 seconds from an instant is not: a day with a
     * 23-hour or 25-hour night lands on the wrong calendar date that way. Two dates differenced as
     * epoch days have no clock in them at all, so there is nothing for a time change to shift.
     */
    val epochDay: Long get() = toLocalDate().toEpochDays()

    companion object {
        fun of(instant: Instant, zone: TimeZone): LocalDay {
            val local = instant.toLocalDateTime(zone)
            // `monthNumber` is deprecated in kotlinx-datetime 0.8.0 and `Month` is an enum
            // starting at JANUARY, so ordinal + 1 gives the calendar month without a warning.
            return LocalDay(local.year, local.month.ordinal + 1, local.day)
        }
    }
}

/** Messages closer together than this belong to the same visual run. */
private const val RUN_GAP_SECONDS = 5 * 60L

/**
 * Builds the rendered timeline from messages ordered NEWEST FIRST.
 *
 * Consecutive messages from one sender within [RUN_GAP_SECONDS] are grouped: only the oldest of
 * a run shows the avatar and name, and only the newest carries the tail. A day change always
 * breaks a run, so a message at 23:59 and one at 00:01 never merge even though they are two
 * minutes apart.
 */
fun buildTimeline(
    messagesNewestFirst: List<Message>,
    zone: TimeZone,
): List<TimelineItem> {
    if (messagesNewestFirst.isEmpty()) return emptyList()

    val items = mutableListOf<TimelineItem>()

    messagesNewestFirst.forEachIndexed { index, message ->
        val day = LocalDay.of(message.sentAt, zone)
        // "Newer" is toward index 0 because the list runs newest-first.
        val newer = messagesNewestFirst.getOrNull(index - 1)
        val older = messagesNewestFirst.getOrNull(index + 1)

        val olderDay = older?.let { LocalDay.of(it.sentAt, zone) }
        val newerDay = newer?.let { LocalDay.of(it.sentAt, zone) }

        val sameRunAsOlder = older != null &&
            older.sender.id == message.sender.id &&
            olderDay == day &&
            message.sentAt.epochSeconds - older.sentAt.epochSeconds <= RUN_GAP_SECONDS

        val sameRunAsNewer = newer != null &&
            newer.sender.id == message.sender.id &&
            newerDay == day &&
            newer.sentAt.epochSeconds - message.sentAt.epochSeconds <= RUN_GAP_SECONDS

        items += TimelineItem.MessageRow(
            message = message,
            // The oldest bubble of a run carries the sender's identity, because visually the
            // run reads downward from it.
            showSender = !sameRunAsOlder,
            isRunStart = !sameRunAsOlder,
            isRunEnd = !sameRunAsNewer,
        )

        // The separator sits AFTER the row in a newest-first list, so that once the list is
        // reversed for display it lands above the first message of that day.
        if (olderDay != day) {
            items += TimelineItem.DaySeparator(day)
        }
    }

    return items
}

/**
 * Merges an incoming server state into what the client already shows.
 *
 * States only move forward. On reconnect the server replays events, so a read receipt can
 * easily arrive before the send response it logically follows -- merging naively would drop a
 * message from READ back to SENT and make the ticks flicker.
 *
 * FAILED sits outside the ladder: a message can fail without ever being SENT, and must never
 * become FAILED once the server has acknowledged it.
 */
fun MessageState.mergeWith(incoming: MessageState): MessageState {
    fun rank(state: MessageState) = when (state) {
        MessageState.SENDING -> 0
        MessageState.SENT -> 1
        MessageState.READ -> 2
        MessageState.FAILED -> -1
    }
    return when {
        // A retry that succeeds lifts a message back out of FAILED.
        this == MessageState.FAILED -> incoming
        // FAILED is reachable only from SENDING.
        incoming == MessageState.FAILED ->
            if (this == MessageState.SENDING) MessageState.FAILED else this
        rank(incoming) > rank(this) -> incoming
        else -> this
    }
}
